// A provider's wallet balance only reflects an outgoing send once it settles
// on-chain, which can lag from seconds to minutes. To avoid showing a stale
// (pre-send) balance in that window we track sends locally and subtract them
// from the displayed balance — self-clearing an entry the moment the provider's
// own figure drops below the balance captured at send time (settlement has
// happened) or a TTL passes (the send never settled, e.g. it failed upstream,
// so the adjustment must not linger). Applied as a React Query `select`, the
// subtraction re-runs on every refetch, so a refetch that still returns the
// pre-send figure cannot revert the displayed balance.

import type { WalletBalancesResponse } from '../api/client.ts';

interface PendingEntry {
  readonly amount: number; // total sent-but-not-yet-settled, in token units
  readonly baseline: number; // raw provider balance captured at send time
  readonly expiresAt: number; // epoch ms after which the entry is dropped
}

const PENDING_TTL_MS = 10 * 60 * 1000;

// Module-level so the adjustment survives navigation (send screen → vault)
// within a session. Lost on a full reload, which is correct: the provider
// balance is the source of truth once the page is reloaded.
const pending = new Map<string, PendingEntry>();

// Records an outgoing send so the displayed balance reflects it immediately.
// `providerAmount` must be the raw (unadjusted) provider balance at send time.
// A second send of the same currency before the first settles accumulates onto
// the existing entry, keeping the original baseline.
export const registerPendingSend = (
  currency: string,
  amount: string,
  providerAmount: string,
  now: number = Date.now(),
): void => {
  const amt = Number(amount);
  if (!Number.isFinite(amt) || amt <= 0) return;
  const existing = pending.get(currency);
  const baseline = existing?.baseline ?? Number(providerAmount);
  if (!Number.isFinite(baseline)) return;
  pending.set(currency, {
    amount: (existing?.amount ?? 0) + amt,
    baseline,
    expiresAt: now + PENDING_TTL_MS,
  });
};

// Test-only reset so module state doesn't leak between cases.
export const __resetPendingSends = (): void => {
  pending.clear();
};

// Subtracts still-pending sends from a balances response, scaling each fiat
// denomination proportionally. Drops an entry once the provider balance has
// fallen below its baseline (settled) or the TTL has elapsed.
export const applyPendingSends = (
  res: WalletBalancesResponse,
  now: number = Date.now(),
): WalletBalancesResponse => {
  if (pending.size === 0) return res;
  return {
    balances: res.balances.map((b) => {
      const entry = pending.get(b.currency);
      if (entry === undefined) return b;
      const provider = Number(b.amount);
      const settled = Number.isFinite(provider) && provider < entry.baseline;
      if (settled || now > entry.expiresAt) {
        pending.delete(b.currency);
        return b;
      }
      if (!Number.isFinite(provider)) return b;
      const after = Math.max(0, provider - entry.amount);
      const ratio = provider > 0 ? after / provider : 0;
      if (b.denominated === undefined) return { ...b, amount: after.toFixed(6) };
      const denominated = Object.fromEntries(
        Object.entries(b.denominated).map(([code, value]) => [
          code,
          (Number(value) * ratio).toFixed(2),
        ]),
      );
      return { ...b, amount: after.toFixed(6), denominated };
    }),
  };
};
